import React, { useContext, useState } from 'react';
import type { FormApi } from 'final-form';
import { Helmet as MetaTags } from 'react-helmet';
import {
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import styled, { css } from 'styled-components';
import { Auth } from '~/amplify';
import { AuthContext } from '~/Root/Auth';
import { Body, Heading1 } from '~/components/atom/Typography';
import { Form, Field } from '~/components/organism/Forms';
import OTPInput from '~/components/molecule/OTPInput';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import AuthFormWrapper from '../components/AuthFormWrapper';
import UnauthNav from '~/components/organism/UnauthNav';
import createPageTitle from '~/util/createPageTitle';
import { reporter } from '~/hooks/useErrorReporter';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import { AUTH_PATHS } from '~/components/page/Auth/constants';
import Catalog from '~/Catalog';
import TEST_ID from './index.testid';
import InfoBlock from '~/components/molecule/InfoBlock';
import { PROFILE_PATH } from '~/components/page/Settings/Profile/constants';
import { MFA_SETTINGS_BLOCK_ID } from '../../Settings/Profile/components/MFASettingsBlock';

const text = {
  title: 'Inloggen met SMS',
  instruction: 'Voer de code in die je per SMS hebt ontvangen om in te loggen.',
  codeSentTo: (phoneNumber: string) => `Code verzonden naar ${phoneNumber}`,
  submitButton: 'Inloggen',
  errorMessage: 'Ongeldige code. Probeer het opnieuw.',
  codeLengthError: 'Voer de volledige 6-cijferige code in.',
  nudge:
    'SMS is alleen een noodoplossing. Na het inloggen vragen we je de authenticator-app in te stellen. Dat is de aanbevolen methode voor tweestapsverificatie.',
};

const CODE_LENGTH = 6;

const validate = ({ code }: { code: string }) => {
  if (!code || code.length !== CODE_LENGTH) {
    return { code: text.codeLengthError };
  }
  return {};
};

type WindowState = {
  from?: string;
  phoneNumber?: string;
};

/**
 * Fallback page reached only when a user somehow ended up with SMS as their only
 * MFA method, causing Cognito to return CONFIRM_SIGN_IN_WITH_SMS_CODE at login.
 * This should never happen, so we report to Sentry whenever a code is submitted here.
 */
const LoginWithSMS: React.FC<RouteComponentProps> = () => {
  const { ensureLogin } = useContext(AuthContext);
  const location = useLocation() as WindowLocation<WindowState>;

  const from = location.state?.from;
  const phoneNumber = location.state?.phoneNumber;

  const [smsError, setSmsError] = useState<string | null>(null);

  const onSubmit = async (
    { code }: { code: string },
    form: FormApi<{ code: string }>,
  ) => {
    setSmsError(null);

    try {
      const signInResult = await Auth.confirmSignIn({
        challengeResponse: code,
      });

      if (signInResult.isSignedIn) {
        const currentUser = await Auth.getCurrentUser();

        // SMS-only MFA should never be possible; flag every submission on this page.
        reporter.captureMessage(
          `LoginWithSMS: User submitted SMS code on SMS-only MFA fallback page (should not happen): ${currentUser?.username}`,
          'error',
        );

        ensureLogin();
        await navigate(`${PROFILE_PATH}#${MFA_SETTINGS_BLOCK_ID}`, {
          replace: true,
          state: { from: AUTH_PATHS.LOGIN_SMS },
        });
        return;
      }

      reporter.captureMessage(
        `LoginWithSMS: SMS verification incomplete: ${signInResult.nextStep.signInStep}`,
        'warning',
      );
      setSmsError(text.errorMessage);
      form.reset({ code: '' });
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { from, message: Catalog.sessionExpired },
        });
        return;
      }

      reporter.captureMessage(
        `LoginWithSMS: Failed to confirmSignIn with SMS: ${err}`,
        'error',
      );
      setSmsError(text.errorMessage);
      form.reset({ code: '' });
    }
  };

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <UnauthNav
        buttons={[
          {
            icon: 'sign-out',
            onClick: () => navigate(AUTH_PATHS.LOGOUT),
            ghost: true,
          },
        ]}
      />
      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={[null, null, 'l']}
          color={{ group: 'accent' }}
          size="xxxl"
          padding={[null, null, null, 'xxxl']}
          skewed
        >
          {text.title}
        </Heading1>

        <Form
          initialValues={{ code: '' }}
          onSubmit={onSubmit}
          validate={validate}
        >
          {({ handleSubmit, submitting, invalid, form }) => (
            <StyledForm onSubmit={handleSubmit}>
              <JustificationContainer
                align="center"
                margin={['xl', null]}
                direction="column"
                gap="l"
                width="100%"
              >
                {phoneNumber && (
                  <Body
                    color={{ group: 'info', variant: 'light' }}
                    size="base"
                    margin={[null]}
                    fontWeight="medium"
                    wordBreak="break-word"
                  >
                    {text.codeSentTo(phoneNumber)}
                  </Body>
                )}

                <Body
                  margin={[null]}
                  color={{ group: 'white' }}
                  size="base"
                  wordBreak="break-word"
                >
                  {text.instruction}
                </Body>

                <InfoBlock
                  appearance="info"
                  padding={['base']}
                  inline
                  width="100%"
                  icon={{
                    name: 'info',
                    backgroundColor: { group: 'info' },
                    color: { group: 'white' },
                  }}
                  headerText={null}
                >
                  <Body
                    margin={[null]}
                    size="base"
                    wordBreak="break-word"
                    color={{ group: 'white' }}
                  >
                    {text.nudge}
                  </Body>
                </InfoBlock>

                {smsError && (
                  <Body
                    margin={[null]}
                    color={{ group: 'danger', variant: 'light' }}
                    size="base"
                    dataTestId={TEST_ID.ERROR_MESSAGE}
                    wordBreak="break-word"
                  >
                    {smsError}
                  </Body>
                )}

                <Field name="code">
                  {({ input }) => (
                    <OTPInput
                      length={CODE_LENGTH}
                      value={input.value ?? ''}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      disabled={submitting}
                      autoFocus
                      onComplete={() => {
                        void form.submit();
                      }}
                    />
                  )}
                </Field>
              </JustificationContainer>

              <JustificationContainer
                gap="base"
                width="100%"
                justification="center"
              >
                <Button
                  appearance="primary"
                  size="medium"
                  type="submit"
                  disabled={submitting || invalid}
                  loading={submitting}
                  label={text.submitButton}
                  dataTestId={TEST_ID.SUBMIT_BUTTON}
                />
              </JustificationContainer>
            </StyledForm>
          )}
        </Form>
      </AuthFormWrapper>
    </>
  );
};

const StyledForm = styled.form(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-width: 0;
    gap: ${theme.space('base')};
  `,
);

export default LoginWithSMS;
